import React, { useMemo, useState, createElement } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from 'recharts';
import { CalendarIcon, TrendingUpIcon, DollarSignIcon, ShoppingBagIcon, CreditCardIcon, Maximize2Icon, Minimize2Icon, DownloadIcon } from 'lucide-react';
import { Order } from './types';
import { useRestaurant } from '../context/RestaurantContext';
interface AnalyticsPanelProps {
  orders: Order[];
  expanded: boolean;
  onToggleExpand: () => void;
}
const COLORS = ['#D4AF37', '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444'];
export function AnalyticsPanel({
  orders,
  expanded,
  onToggleExpand
}: AnalyticsPanelProps) {
  const {
    restaurant
  } = useRestaurant();
  const currencySymbol = restaurant?.currency_symbol || '₡';
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const filteredOrders = useMemo(() => {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    return orders.filter(o => {
      if (o.status === 'cancelled') return false;
      const orderDate = new Date(o.created_at);
      return orderDate >= start && orderDate <= end;
    });
  }, [orders, startDate, endDate]);
  const stats = useMemo(() => {
    const totalRevenue = filteredOrders.reduce((sum, o) => sum + o.total_final, 0);
    const totalOrders = filteredOrders.length;
    const avgTicket = totalOrders > 0 ? totalRevenue / totalOrders : 0;
    return {
      totalRevenue,
      totalOrders,
      avgTicket
    };
  }, [filteredOrders]);
  const serviceStats = useMemo(() => {
    const tableOrders = filteredOrders.filter(o => o.table_number);
    const qrOrders = tableOrders.filter(o => o.service_mode === 'qr');
    const staffOrders = tableOrders.filter(o => o.service_mode === 'staff');
    const unknownOrders = tableOrders.filter(o => !o.service_mode);
    const totalTable = tableOrders.length;
    const qrPercent = totalTable > 0 ? Math.round(qrOrders.length / totalTable * 100) : 0;
    const qrTables = new Set(qrOrders.map(o => o.table_number)).size;
    const staffTables = new Set(staffOrders.map(o => o.table_number)).size;
    const qrRevenue = qrOrders.reduce((sum, o) => sum + o.total_final, 0);
    const staffRevenue = staffOrders.reduce((sum, o) => sum + o.total_final, 0);
    return {
      totalTable,
      qrOrders: qrOrders.length,
      staffOrders: staffOrders.length,
      unknownOrders: unknownOrders.length,
      qrPercent,
      qrTables,
      staffTables,
      qrRevenue,
      staffRevenue
    };
  }, [filteredOrders]);
  const serviceModeData = useMemo(() => {
    const data = [];
    if (serviceStats.qrOrders > 0) data.push({
      name: 'QR (Autoatención)',
      value: serviceStats.qrOrders
    });
    if (serviceStats.staffOrders > 0) data.push({
      name: 'Staff (Personal)',
      value: serviceStats.staffOrders
    });
    if (serviceStats.unknownOrders > 0) data.push({
      name: 'Sin clasificar',
      value: serviceStats.unknownOrders
    });
    return data;
  }, [serviceStats]);
  const revenueByDate = useMemo(() => {
    const data: Record<string, number> = {};
    filteredOrders.forEach(o => {
      const date = new Date(o.created_at).toLocaleDateString('es-CR', {
        month: 'short',
        day: 'numeric'
      });
      data[date] = (data[date] || 0) + o.total_final;
    });
    return Object.entries(data).map(([date, revenue]) => ({
      date,
      revenue
    })).reverse();
  }, [filteredOrders]);
  const paymentMethods = useMemo(() => {
    const data: Record<string, number> = {};
    filteredOrders.forEach(o => {
      const method = o.payment_method || 'Otro';
      data[method] = (data[method] || 0) + 1;
    });
    return Object.entries(data).map(([name, value]) => ({
      name,
      value
    })).sort((a, b) => b.value - a.value);
  }, [filteredOrders]);
  const salesByDay = useMemo(() => {
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const data: Record<string, {
      name: string;
      orders: number;
      revenue: number;
    }> = {};
    days.forEach(d => data[d] = {
      name: d,
      orders: 0,
      revenue: 0
    });
    filteredOrders.forEach(o => {
      const dayName = days[new Date(o.created_at).getDay()];
      data[dayName].orders += 1;
      data[dayName].revenue += o.total_final;
    });
    return [data['Lunes'], data['Martes'], data['Miércoles'], data['Jueves'], data['Viernes'], data['Sábado'], data['Domingo']];
  }, [filteredOrders]);
  const productStats = useMemo(() => {
    const products: Record<string, number> = {};
    filteredOrders.forEach(o => {
      const lines = o.order_text.split('\n');
      lines.forEach(line => {
        const match = line.match(/(?:^\d+\.\s*)?(.+?)(?:\s*\(?x(\d+)\)?)?\s*—\s*[₡$]/);
        if (match) {
          const name = match[1].trim();
          const qty = match[2] ? parseInt(match[2], 10) : 1;
          if (name.toLowerCase() !== 'total' && name.toLowerCase() !== 'express') {
            products[name] = (products[name] || 0) + qty;
          }
        }
      });
    });
    const sortedProducts = Object.entries(products).map(([name, quantity]) => ({
      name,
      quantity
    })).sort((a, b) => b.quantity - a.quantity);
    return {
      top: sortedProducts.slice(0, 5),
      bottom: sortedProducts.slice(-5).reverse().filter(p => p.quantity > 0)
    };
  }, [filteredOrders]);
  const exportToCSV = () => {
    let csv = 'Fecha,Orden,Cliente,Entrega,Metodo Pago,Subtotal,Express,Total\n';
    filteredOrders.forEach(o => {
      const date = new Date(o.created_at).toLocaleString('es-CR');
      const name = `"${o.customer_name.replace(/"/g, '""')}"`;
      csv += `${date},${o.order_number},${name},${o.delivery_type},${o.payment_method},${o.subtotal},${o.express_fee},${o.total_final}\n`;
    });
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `reporte_ventas_${startDate}_al_${endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-surface border border-gray-700 p-3 rounded-lg shadow-xl" data-id="element-0">
          <p className="text-gray-300 font-medium mb-1" data-id="element-1">{label}</p>
          {payload.map((entry: any, index: number) => <p key={index} className="text-sm" style={{
          color: entry.color
        }} data-id="element-2">
              {entry.name === 'revenue' || entry.name === 'Ingresos' ? `${currencySymbol}${entry.value.toLocaleString()}` : `${entry.value} unidades`}
            </p>)}
        </div>;
    }
    return null;
  };
  return <div className={`bg-surface border border-gray-800 rounded-2xl flex flex-col transition-all duration-300 ${expanded ? 'fixed inset-4 z-50 shadow-2xl overflow-hidden' : 'relative w-full'}`} data-id="element-3">
      <div className="flex flex-wrap items-center justify-between p-4 sm:p-6 border-b border-gray-800 gap-4 shrink-0" data-id="element-4">
        <div data-id="element-5">
          <h2 className="font-heading text-xl text-white flex items-center gap-2" data-id="element-6">
            <TrendingUpIcon className="w-5 h-5 text-dorado" data-id="element-7" />
            Reportes y Contabilidad
          </h2>
          <p className="text-sm text-gray-500 mt-1" data-id="element-8">
            Análisis de ventas, productos y métodos de pago
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3" data-id="element-9">
          <div className="flex items-center gap-2 bg-carbon rounded-lg p-1 border border-gray-800" data-id="element-10">
            <div className="flex items-center gap-2 px-3 py-1.5" data-id="element-11">
              <CalendarIcon className="w-4 h-4 text-gray-400" data-id="element-12" />
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-transparent text-sm text-white border-none focus:ring-0 p-0 w-28" data-id="element-13" />
            </div>
            <span className="text-gray-600" data-id="element-14">-</span>
            <div className="px-3 py-1.5" data-id="element-15">
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-transparent text-sm text-white border-none focus:ring-0 p-0 w-28" data-id="element-16" />
            </div>
          </div>
          <button onClick={exportToCSV} className="flex items-center gap-2 px-4 py-2 bg-dorado/10 text-dorado border border-dorado/20 rounded-lg hover:bg-dorado/20 transition-colors text-sm font-medium" data-id="element-17">
            <DownloadIcon className="w-4 h-4" data-id="element-18" />
            <span className="hidden sm:inline" data-id="element-19">Exportar CSV</span>
          </button>
          <button onClick={onToggleExpand} className="p-2 bg-surface-light rounded-lg text-gray-400 hover:text-white transition-colors border border-gray-700" title={expanded ? 'Minimizar' : 'Maximizar'} data-id="element-20">
            {expanded ? <Minimize2Icon className="w-5 h-5" data-id="element-21" /> : <Maximize2Icon className="w-5 h-5" data-id="element-22" />}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6" data-id="element-23">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-24">
          <div className="bg-carbon rounded-xl p-5 border border-gray-800 flex items-center gap-4" data-id="element-25">
            <div className="w-12 h-12 bg-emerald-500/10 rounded-full flex items-center justify-center border border-emerald-500/20 shrink-0" data-id="element-26">
              <DollarSignIcon className="w-6 h-6 text-emerald-500" data-id="element-27" />
            </div>
            <div data-id="element-28">
              <p className="text-sm text-gray-500 mb-1" data-id="element-29">Ingresos Totales</p>
              <p className="text-2xl font-bold text-white" data-id="element-30">
                {currencySymbol}
                {stats.totalRevenue.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="bg-carbon rounded-xl p-5 border border-gray-800 flex items-center gap-4" data-id="element-31">
            <div className="w-12 h-12 bg-blue-500/10 rounded-full flex items-center justify-center border border-blue-500/20 shrink-0" data-id="element-32">
              <ShoppingBagIcon className="w-6 h-6 text-blue-500" data-id="element-33" />
            </div>
            <div data-id="element-34">
              <p className="text-sm text-gray-500 mb-1" data-id="element-35">Pedidos Completados</p>
              <p className="text-2xl font-bold text-white" data-id="element-36">
                {stats.totalOrders}
              </p>
            </div>
          </div>
          <div className="bg-carbon rounded-xl p-5 border border-gray-800 flex items-center gap-4" data-id="element-37">
            <div className="w-12 h-12 bg-purple-500/10 rounded-full flex items-center justify-center border border-purple-500/20 shrink-0" data-id="element-38">
              <CreditCardIcon className="w-6 h-6 text-purple-500" data-id="element-39" />
            </div>
            <div data-id="element-40">
              <p className="text-sm text-gray-500 mb-1" data-id="element-41">Ticket Promedio</p>
              <p className="text-2xl font-bold text-white" data-id="element-42">
                {currencySymbol}
                {Math.round(stats.avgTicket).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {serviceStats.totalTable > 0 && <div className="bg-carbon rounded-xl p-5 border border-gray-800" data-id="element-43">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-5 flex items-center gap-2" data-id="element-44">
              📱 Autoatención vs Personal
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5" data-id="element-45">
              <div className="bg-surface rounded-lg p-4 border border-gray-700 text-center" data-id="element-46">
                <p className="text-2xl font-bold text-cyan-400" data-id="element-47">
                  {serviceStats.qrOrders}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-48">Pedidos QR</p>
              </div>
              <div className="bg-surface rounded-lg p-4 border border-gray-700 text-center" data-id="element-49">
                <p className="text-2xl font-bold text-purple-400" data-id="element-50">
                  {serviceStats.staffOrders}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-51">Pedidos Staff</p>
              </div>
              <div className="bg-surface rounded-lg p-4 border border-gray-700 text-center" data-id="element-52">
                <p className="text-2xl font-bold text-dorado" data-id="element-53">
                  {serviceStats.qrPercent}%
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-54">Autoatención</p>
              </div>
              <div className="bg-surface rounded-lg p-4 border border-gray-700 text-center" data-id="element-55">
                <p className="text-2xl font-bold text-white" data-id="element-56">
                  {serviceStats.totalTable}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-57">Total en Mesa</p>
              </div>
            </div>
            {serviceModeData.length > 0 && <div className="flex items-center justify-center" data-id="element-58">
                <div className="w-full max-w-xs h-48" data-id="element-59">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-60">
                    <PieChart data-id="element-61">
                      <Pie data={serviceModeData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value" data-id="element-62">
                        {serviceModeData.map((_, index) => <Cell key={index} fill={['#22D3EE', '#A855F7', '#6B7280'][index]} data-id="element-63" />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip data-id="element-65" />} data-id="element-64" />
                      <Legend verticalAlign="bottom" height={36} formatter={(value: string) => <span className="text-xs text-gray-400" data-id="element-67">{value}</span>} data-id="element-66" />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>}
          </div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-68">
          <div className="lg:col-span-2 bg-carbon rounded-xl p-5 border border-gray-800" data-id="element-69">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-6" data-id="element-70">
              Ingresos por Fecha
            </h3>
            <div className="h-72 w-full" data-id="element-71">
              {revenueByDate.length > 0 ? <ResponsiveContainer width="100%" height="100%" data-id="element-72">
                  <LineChart data={revenueByDate} margin={{
                top: 5,
                right: 20,
                bottom: 5,
                left: 0
              }} data-id="element-73">
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} data-id="element-74" />
                    <XAxis dataKey="date" stroke="#9CA3AF" fontSize={12} tickLine={false} axisLine={false} data-id="element-75" />
                    <YAxis stroke="#9CA3AF" fontSize={12} tickLine={false} axisLine={false} tickFormatter={value => `${currencySymbol}${value / 1000}k`} data-id="element-76" />
                    <Tooltip content={<CustomTooltip data-id="element-78" />} data-id="element-77" />
                    <Line type="monotone" dataKey="revenue" name="Ingresos" stroke="#D4AF37" strokeWidth={3} dot={{
                  fill: '#D4AF37',
                  strokeWidth: 2,
                  r: 4
                }} activeDot={{
                  r: 6,
                  fill: '#D4AF37',
                  stroke: '#0F0F0F',
                  strokeWidth: 2
                }} data-id="element-79" />
                  </LineChart>
                </ResponsiveContainer> : <div className="h-full flex items-center justify-center text-gray-500" data-id="element-80">
                  No hay datos para este período
                </div>}
            </div>
          </div>
          <div className="bg-carbon rounded-xl p-5 border border-gray-800" data-id="element-81">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-6" data-id="element-82">
              Métodos de Pago
            </h3>
            <div className="h-72 w-full" data-id="element-83">
              {paymentMethods.length > 0 ? <ResponsiveContainer width="100%" height="100%" data-id="element-84">
                  <PieChart data-id="element-85">
                    <Pie data={paymentMethods} cx="50%" cy="45%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" data-id="element-86">
                      {paymentMethods.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-87" />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip data-id="element-89" />} data-id="element-88" />
                    <Legend verticalAlign="bottom" height={36} iconType="circle" data-id="element-90" />
                  </PieChart>
                </ResponsiveContainer> : <div className="h-full flex items-center justify-center text-gray-500" data-id="element-91">
                  No hay datos
                </div>}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-92">
          <div className="bg-carbon rounded-xl p-5 border border-gray-800" data-id="element-93">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-6 flex items-center gap-2" data-id="element-94">
              <span className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-95" /> Productos
              Más Vendidos
            </h3>
            <div className="h-64 w-full" data-id="element-96">
              {productStats.top.length > 0 ? <ResponsiveContainer width="100%" height="100%" data-id="element-97">
                  <BarChart data={productStats.top} layout="vertical" margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5
              }} data-id="element-98">
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={false} data-id="element-99" />
                    <XAxis type="number" stroke="#9CA3AF" fontSize={12} data-id="element-100" />
                    <YAxis dataKey="name" type="category" stroke="#9CA3AF" fontSize={12} width={100} data-id="element-101" />
                    <Tooltip content={<CustomTooltip data-id="element-103" />} data-id="element-102" />
                    <Bar dataKey="quantity" name="Unidades" fill="#10B981" radius={[0, 4, 4, 0]} barSize={24} data-id="element-104" />
                  </BarChart>
                </ResponsiveContainer> : <div className="h-full flex items-center justify-center text-gray-500" data-id="element-105">
                  No hay datos
                </div>}
            </div>
          </div>
          <div className="bg-carbon rounded-xl p-5 border border-gray-800" data-id="element-106">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-6 flex items-center gap-2" data-id="element-107">
              <span className="w-2 h-2 rounded-full bg-red-500" data-id="element-108" /> Productos
              Menos Vendidos
            </h3>
            <div className="h-64 w-full" data-id="element-109">
              {productStats.bottom.length > 0 ? <ResponsiveContainer width="100%" height="100%" data-id="element-110">
                  <BarChart data={productStats.bottom} layout="vertical" margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5
              }} data-id="element-111">
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={false} data-id="element-112" />
                    <XAxis type="number" stroke="#9CA3AF" fontSize={12} data-id="element-113" />
                    <YAxis dataKey="name" type="category" stroke="#9CA3AF" fontSize={12} width={100} data-id="element-114" />
                    <Tooltip content={<CustomTooltip data-id="element-116" />} data-id="element-115" />
                    <Bar dataKey="quantity" name="Unidades" fill="#EF4444" radius={[0, 4, 4, 0]} barSize={24} data-id="element-117" />
                  </BarChart>
                </ResponsiveContainer> : <div className="h-full flex items-center justify-center text-gray-500" data-id="element-118">
                  No hay datos
                </div>}
            </div>
          </div>
        </div>

        <div className="bg-carbon rounded-xl p-5 border border-gray-800" data-id="element-119">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-6" data-id="element-120">
            Días de Mayor Venta (Ingresos)
          </h3>
          <div className="h-72 w-full" data-id="element-121">
            <ResponsiveContainer width="100%" height="100%" data-id="element-122">
              <BarChart data={salesByDay} margin={{
              top: 5,
              right: 20,
              bottom: 5,
              left: 0
            }} data-id="element-123">
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} data-id="element-124" />
                <XAxis dataKey="name" stroke="#9CA3AF" fontSize={12} data-id="element-125" />
                <YAxis stroke="#9CA3AF" fontSize={12} tickFormatter={value => `${currencySymbol}${value / 1000}k`} data-id="element-126" />
                <Tooltip content={<CustomTooltip data-id="element-128" />} data-id="element-127" />
                <Bar dataKey="revenue" name="Ingresos" fill="#3B82F6" radius={[4, 4, 0, 0]} barSize={40} data-id="element-129" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>;
}